import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  buttonContainerStyle,
  containerStyle
} from './marketplace-app-selector.styles';
import SearchForm from './search-form/search-form';
import { Button, ButtonProps } from '@atlassiansox/wpl-utils';
import { ProductFamilyName } from '../../models';
import {
  WithAnalyticsEventsProps,
  withAnalyticsEvents
} from '@atlaskit/analytics-next';
import { ANALYTICS_EVENTS } from '../../constants';
import { useModuleProps } from '../../module-props';

export type MarketplaceAppSelectorProps = {
  parentProductName: ProductFamilyName;
};

const AddButtonBase = ({
  createAnalyticsEvent,
  onClick,
  parentProductName,
  ...rest
}: WithAnalyticsEventsProps &
  ButtonProps & { parentProductName: ProductFamilyName }) => {
  const handleClick = useCallback(
    async (event: MouseEvent) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: ANALYTICS_EVENTS.openAppSelection,
        parentProductName
      });
      analyticsEvent?.fire();

      if (onClick) {
        onClick(event);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return <Button {...rest} onClick={handleClick} />;
};

const AddButton = withAnalyticsEvents()(AddButtonBase);

const MarketplaceAppSelector = ({
  parentProductName
}: MarketplaceAppSelectorProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addMarketplaceAppsLabel } = useModuleProps();

  useEffect(() => {
    const handleClickOutside: EventListener = (event: Event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        isModalOpen
      ) {
        setIsModalOpen(!isModalOpen);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);
  return (
    <div css={containerStyle} data-testid="marketplace-app-selector">
      <div ref={modalRef} css={buttonContainerStyle}>
        <AddButton
          text={`+ ${addMarketplaceAppsLabel}`}
          type="secondary"
          parentProductName={parentProductName}
          onClick={() => setIsModalOpen(!isModalOpen)}
        />
        {isModalOpen && (
          <SearchForm
            parentProductName={parentProductName}
            closeModal={() => setIsModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default MarketplaceAppSelector;
